import { useState, useEffect } from 'react'
import { useAppSelector } from '../hooks/redux'
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline'
import { format, startOfDay, subDays, addDays, isToday, differenceInDays, startOfMonth, endOfMonth, addMonths, subMonths, eachDayOfInterval } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { Issue } from '@/types'
import * as issueApi from '../services/api/issue'

// 時間刻度選項
const TIME_SCALES = [
  { value: 'day', label: '日' },
  { value: 'week', label: '週' },
  { value: 'month', label: '月' },
  { value: 'quarter', label: '季' },
]

// 狀態顏色
const STATUS_COLORS: Record<string, string> = {
  'TODO': 'bg-gray-400',
  'IN_PROGRESS': 'bg-blue-500',
  'IN_REVIEW': 'bg-purple-500',
  'DONE': 'bg-green-600',
}

interface TimelineIssue extends Issue {
  startDate?: Date
  endDate?: Date
}

const ProjectTimeline = () => {
  const { currentProject } = useAppSelector(state => state.project)
  const [issues, setIssues] = useState<TimelineIssue[]>([])
  const [loading, setLoading] = useState(true)
  const [timeScale, setTimeScale] = useState('month')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewStart, setViewStart] = useState(startOfMonth(new Date()))
  const [viewEnd, setViewEnd] = useState(endOfMonth(new Date()))
  const [searchQuery, setSearchQuery] = useState('')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [hoveredIssue, setHoveredIssue] = useState<string | null>(null)

  // 獲取專案議題
  useEffect(() => {
    if (currentProject) {
      fetchProjectIssues()
    }
  }, [currentProject])

  // 根據時間刻度調整視圖範圍
  useEffect(() => {
    switch (timeScale) {
      case 'day':
        // 日視圖：顯示當前日期前後各3天，總共7天
        setViewStart(subDays(currentDate, 3))
        setViewEnd(addDays(currentDate, 3))
        break
      case 'week':
        // 週視圖：顯示當前日期前後各7天，總共15天
        setViewStart(subDays(currentDate, 7))
        setViewEnd(addDays(currentDate, 7))
        break
      case 'month':
        setViewStart(startOfMonth(currentDate))
        setViewEnd(endOfMonth(currentDate))
        break
      case 'quarter':
        // 季度顯示三個月：當前月份的前一個月到後一個月
        setViewStart(startOfMonth(subMonths(currentDate, 1)))
        setViewEnd(endOfMonth(addMonths(currentDate, 1)))
        break
    }
  }, [currentDate, timeScale])

  const fetchProjectIssues = async () => {
    if (!currentProject) return

    try {
      setLoading(true)
      const response = await issueApi.getProjectIssues(currentProject.id)

      if (response.success && response.data) {
        // 轉換議題資料，包含起始日和到期日
        const timelineIssues: TimelineIssue[] = response.data.issues.map(issue => {
          // 使用 createdAt 作為開始日期，dueDate 作為結束日期
          // 如果沒有 dueDate，則使用 createdAt 當天作為結束日期
          const startDate = issue.createdAt ? startOfDay(new Date(issue.createdAt)) : undefined
          const endDate = issue.dueDate ? startOfDay(new Date(issue.dueDate)) : startDate
          
          return {
            ...issue,
            startDate,
            endDate,
          }
        })
        setIssues(timelineIssues)
      }
    } catch (error) {
      console.error('獲取專案議題失敗:', error)
    } finally {
      setLoading(false)
    }
  }

  // 過濾議題
  const filteredIssues = issues.filter(issue => {
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      return (
        issue.key.toLowerCase().includes(searchLower) ||
        issue.title.toLowerCase().includes(searchLower) ||
        issue.assignee?.name.toLowerCase().includes(searchLower)
      )
    }
    return true
  })

  // 只顯示有日期範圍或至少有起始日的議題
  const timelineIssues = filteredIssues.filter(issue => issue.startDate)

  // 生成日期刻度
  const generateDateScale = () => {
    if (timeScale === 'quarter') {
      // 季度視圖不需要日期刻度，只需要月份
      return []
    }
    return eachDayOfInterval({ start: viewStart, end: viewEnd })
  }

  // 生成月份標題
  const generateMonthHeaders = () => {
    if (timeScale === 'quarter') {
      // 季度視圖：直接生成三個月份
      const months = []
      for (let i = -1; i <= 1; i++) {
        const monthDate = addMonths(currentDate, i)
        months.push({
          label: format(monthDate, 'MMMM'),
          width: 33.33 // 三個月平均分配
        })
      }
      return months
    }

    const dateScale = generateDateScale()
    if (dateScale.length === 0) return []

    const monthHeaders: { label: string; width: number }[] = []
    let currentMonth = ''
    let daysInMonth = 0
    const totalDays = dateScale.length

    dateScale.forEach((date, index) => {
      const monthLabel = format(date, 'MMMM') // 使用英文月份名稱
      
      if (monthLabel !== currentMonth) {
        if (currentMonth && daysInMonth > 0) {
          monthHeaders.push({
            label: currentMonth,
            width: (daysInMonth / totalDays) * 100
          })
        }
        currentMonth = monthLabel
        daysInMonth = 1
      } else {
        daysInMonth++
      }

      // 最後一天
      if (index === dateScale.length - 1 && daysInMonth > 0) {
        monthHeaders.push({
          label: currentMonth,
          width: (daysInMonth / totalDays) * 100
        })
      }
    })

    return monthHeaders
  }

  // 導航功能
  const navigate = (direction: 'prev' | 'next') => {
    switch (timeScale) {
      case 'day':
      case 'week':
        setCurrentDate(prev => direction === 'prev' ? subDays(prev, 7) : addDays(prev, 7))
        break
      case 'month':
        setCurrentDate(prev => direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1))
        break
      case 'quarter':
        setCurrentDate(prev => direction === 'prev' ? subMonths(prev, 3) : addMonths(prev, 3))
        break
    }
  }

  const navigateToToday = () => {
    setCurrentDate(new Date())
  }

  // 計算甘特條的位置和寬度
  const calculateBarPosition = (issue: TimelineIssue) => {
    if (!issue.startDate) return null;

    const startDate = startOfDay(issue.startDate);
    const endDate = issue.endDate ? startOfDay(issue.endDate) : startDate;

    // 確保議題在當前視圖範圍內
    if (endDate < viewStart || startDate > viewEnd) {
      return null;
    }

    const totalDaysInView = differenceInDays(viewEnd, viewStart) + 1;
    if (totalDaysInView <= 0) return null;

    const cellWidth = 100 / totalDaysInView;

    // 計算起始日的偏移量（天數）
    const startOffset = differenceInDays(startDate, viewStart);

    // 計算結束日的偏移量。我們使用 addDays(endDate, 1) 來確保結束日期當天被完全包含。
    const endOffset = differenceInDays(addDays(endDate, 1), viewStart);

    // 將偏移量限制在可視範圍內
    const visibleStartOffset = Math.max(0, startOffset);
    const visibleEndOffset = Math.min(totalDaysInView, endOffset);

    // 計算可視的持續時間
    const visibleDuration = visibleEndOffset - visibleStartOffset;

    if (visibleDuration <= 0) return null;

    const left = visibleStartOffset * cellWidth;
    const width = visibleDuration * cellWidth;

    return { left, width };
  };

  // 獲取狀態顏色
  const getStatusColor = (status: string) => {
    return STATUS_COLORS[status] || 'bg-gray-400'
  }

  const dateScale = generateDateScale()

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const handleExport = () => {
    console.log('匯出甘特圖')
  }

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white' : ''} flex flex-col h-full`}>
      {/* 頂部工具欄 */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* 搜尋框 */}
            <div className="relative">
              <input
                type="text"
                placeholder="搜尋議題"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64 pl-3 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* 時間刻度選擇 */}
            <div className="flex items-center bg-gray-100 rounded-md">
              {TIME_SCALES.map(scale => (
                <button
                  key={scale.value}
                  onClick={() => setTimeScale(scale.value)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    timeScale === scale.value
                      ? 'bg-white text-primary-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {scale.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* 導航按鈕 */}
            <button
              onClick={() => navigate('prev')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={navigateToToday}
              className="px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-md"
            >
              今天
            </button>
            <button
              onClick={() => navigate('next')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>

            {/* 功能按鈕 */}
            <button
              onClick={handleExport}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              title="匯出"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              title="全螢幕"
            >
              <ArrowsPointingOutIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* 甘特圖主體 */}
      <div className="flex-1 overflow-auto bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="min-h-full">
            {/* 時間刻度標題 */}
            <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
              <div className="flex">
                <div className="w-64 flex-shrink-0 px-4 py-3 border-r border-gray-200">
                  <span className="text-sm font-medium text-gray-700">項目</span>
                </div>
                <div className="flex-1">
                  {/* 月份標題行 */}
                  {timeScale === 'month' || timeScale === 'quarter' ? (
                    <div className="flex border-b border-gray-200">
                      {generateMonthHeaders().map((monthHeader, index) => (
                        <div
                          key={index}
                          className="text-center py-2 border-r border-gray-200 bg-gray-50"
                          style={{ width: `${monthHeader.width}%` }}
                        >
                          <span className="text-sm font-medium text-gray-700">
                            {monthHeader.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {/* 日期刻度行 */}
                  {timeScale !== 'quarter' && (
                    <div className="flex relative">
                      {dateScale.map((date, index) => (
                        <div
                          key={index}
                          className={`flex-1 px-1 py-2 border-r border-gray-200 text-center relative ${
                            isToday(date) ? 'bg-primary-50' : ''
                          }`}
                          style={{ minWidth: timeScale === 'day' || timeScale === 'week' ? '60px' : '30px' }}
                        >
                          {timeScale === 'day' || timeScale === 'week' ? (
                            <>
                              <div className="text-xs text-gray-500">
                                {format(date, 'M月', { locale: zhTW })}
                              </div>
                              <div className="text-sm font-medium">
                                {format(date, 'd')}
                              </div>
                            </>
                          ) : (
                            <div className="text-xs font-medium">
                              {format(date, 'd')}
                            </div>
                          )}
                        </div>
                      ))}
                      {/* 今天標記線 - 統一處理 */}
                      {(() => {
                        const today = startOfDay(new Date())
                        if (today >= viewStart && today <= viewEnd) {
                          const totalDays = differenceInDays(viewEnd, viewStart) + 1
                          const todayOffset = differenceInDays(today, viewStart) + 0.5 // 加0.5讓線條在日期中間
                          const leftPercent = (todayOffset / totalDays) * 100
                          
                          
                          return (
                            <div 
                              className="absolute top-0 bottom-0 w-0.5 bg-primary-600 pointer-events-none z-30"
                              style={{ left: `${leftPercent}%` }}
                            />
                          )
                        }
                        return null
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 甘特圖內容 */}
            <div className="divide-y divide-gray-200">
              {timelineIssues.map((issue) => {
                const barPosition = calculateBarPosition(issue)
                
                return (
                  <div 
                    key={issue.id} 
                    className="flex hover:bg-gray-50"
                    onMouseEnter={() => setHoveredIssue(issue.id)}
                    onMouseLeave={() => setHoveredIssue(null)}
                  >
                    {/* 議題資訊 */}
                    <div className="w-64 flex-shrink-0 px-4 py-3 border-r border-gray-200">
                      <div className="flex items-start space-x-2">
                        <input
                          type="checkbox"
                          className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={false}
                          readOnly
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {issue.key} {issue.title}
                          </div>
                          {issue.assignee && (
                            <div className="flex items-center space-x-1 mt-1">
                              <span className="text-xs text-gray-400">
                                <svg className="w-3 h-3 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {issue.assignee.name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 甘特條 */}
                    <div className="flex-1 relative h-16 flex items-center">
                      <div className="absolute inset-0 flex">
                        {timeScale === 'quarter' ? (
                          // 季度視圖：顯示月份分隔
                          <>
                            {generateMonthHeaders().map((monthHeader, index) => (
                              <div
                                key={index}
                                className="border-r border-gray-200"
                                style={{ width: `${monthHeader.width}%` }}
                              />
                            ))}
                            {/* 今天標記：在整個季度範圍內顯示 */}
                            {(() => {
                              const today = startOfDay(new Date())
                              if (today >= viewStart && today <= viewEnd) {
                                const totalDays = differenceInDays(viewEnd, viewStart) + 1
                                const todayOffset = differenceInDays(today, viewStart) + 0.5 // 加0.5讓線條在日期中間
                                const leftPercent = (todayOffset / totalDays) * 100
                                return (
                                  <div 
                                    className="absolute top-0 bottom-0 w-0.5 bg-primary-600 pointer-events-none z-30"
                                    style={{ left: `${leftPercent}%` }}
                                  />
                                )
                              }
                              return null
                            })()}
                          </>
                        ) : (
                          // 其他視圖：顯示日期分隔
                          <>
                            {dateScale.map((date, index) => (
                              <div
                                key={index}
                                className={`flex-1 border-r border-gray-200 ${
                                  isToday(date) ? 'bg-primary-50' : ''
                                }`}
                                style={{ minWidth: timeScale === 'day' || timeScale === 'week' ? '60px' : '30px' }}
                              />
                            ))}
                            {/* 今天標記：使用正確的位置計算 */}
                            {(() => {
                              const today = startOfDay(new Date())
                              if (today >= viewStart && today <= viewEnd) {
                                const totalDays = differenceInDays(viewEnd, viewStart) + 1
                                const todayOffset = differenceInDays(today, viewStart) + 0.5 // 加0.5讓線條在日期中間
                                const leftPercent = (todayOffset / totalDays) * 100
                                return (
                                  <div 
                                    className="absolute top-0 bottom-0 w-0.5 bg-primary-600 pointer-events-none z-30"
                                    style={{ left: `${leftPercent}%` }}
                                  />
                                )
                              }
                              return null
                            })()}
                          </>
                        )}
                      </div>
                      
                      {barPosition && (
                        <div
                          className={`absolute h-8 rounded-md ${getStatusColor(issue.status)} ${
                            hoveredIssue === issue.id ? 'shadow-lg z-20' : 'z-10'
                          } transition-all cursor-pointer`}
                          style={{
                            left: `${barPosition.left}%`,
                            width: `${barPosition.width}%`,
                          }}
                          title={`${issue.key}: ${issue.title}
起始日：${issue.startDate ? format(issue.startDate, 'yyyy/MM/dd') : '未設定'}
到期日：${issue.endDate ? format(issue.endDate, 'yyyy/MM/dd') : '未設定'}
狀態：${issue.status}
負責人：${issue.assignee?.name || '未指派'}`}
                        >
                          <div className="px-2 h-full flex items-center justify-between">
                            <span className="text-xs text-white font-medium truncate">
                              {issue.key}
                            </span>
                            {issue.assignee && (
                              <div className="flex-shrink-0 ml-1">
                                {issue.assignee.avatar ? (
                                  <img 
                                    src={issue.assignee.avatar} 
                                    alt={issue.assignee.name}
                                    className="w-5 h-5 rounded-full border border-white"
                                  />
                                ) : (
                                  <div className="w-5 h-5 rounded-full bg-white bg-opacity-30 flex items-center justify-center border border-white">
                                    <span className="text-xs text-white font-medium">
                                      {issue.assignee.name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {timelineIssues.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>沒有設定日期的議題</p>
              </div>
            )}

            {/* 建立新議題按鈕 */}
            <div className="flex border-t border-gray-200">
              <div className="w-64 flex-shrink-0 px-4 py-3 border-r border-gray-200">
                <button className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900">
                  <span className="text-lg">+</span>
                  <span>建立</span>
                </button>
              </div>
              <div className="flex-1">
                {/* 空白區域 */}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectTimeline
